import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { DateTime } from 'luxon'
import config from '../../../config'
import type {
    AdjuntoDTO,
    CreateAdjuntoRequest,
    CreateCRMRequest,
    CRMDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    FormaPago,
    MonedaDTO,
    PolizaDTO,
    PolizaVehiculoDTO,
    ReciboDTO,
    StorageFile,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { daysFromToday, formatCompactDate, formatSpokenDate, parseDate } from '../../../utils/dates'
import type { DateInput } from '../../../utils/dates'
import { labelize, statusKey } from '../../customers/common/customer-common'
import type { NoteType } from '../../customers/common/customer-common'

export {
    BITACORA_FILTERS,
    chipClass,
    errorMessage,
    formatActivityDate,
    formatCount,
    formatSpokenDate,
    labelize,
    money,
    noteMeta,
    pageWindow,
    personName,
    RamoMark,
    ramoName,
    statusKey,
} from '../../customers/common/customer-common'
export type { BitacoraFilter, NoteMeta, NoteType } from '../../customers/common/customer-common'
export { formatCompactDate }

export const RAMOS = ['Vida', 'Auto', 'Salud', 'Diversos']

export interface FormaPagoOption {
    key: FormaPago
    label: string
    parcialidades: number
}

export const FORMA_PAGO: FormaPagoOption[] = [
    { key: 'contado', label: 'Anual', parcialidades: 1 },
    { key: 'semestral', label: 'Semestral', parcialidades: 2 },
    { key: 'trimestral', label: 'Trimestral', parcialidades: 4 },
    { key: 'mensual', label: 'Mensual', parcialidades: 12 },
]

export const POLICIES_PATH = '/polizas'
export const NEW_POLICY_PATH = `${POLICIES_PATH}/nuevo`

export interface PolicyFormLocationState {
    backUrl?: string
    action?: 'renovar'
    polizaId?: number
}

export interface PolicyReadLocationState {
    savedMessage?: string
}

export const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

export type LoadState = 'idle' | 'loading' | 'ready' | 'error'

export const isBusy = (state: LoadState) => state === 'idle' || state === 'loading'

export const fileSizeLabel = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString('es-MX', { maximumFractionDigits: 1 })} MB`
    return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('es-MX')} KB`
}

export const fileExt = (name?: string | null) => String(name || '').split('.').pop()?.slice(0, 3).toUpperCase() || 'FILE'

export const extTone = (ext: string) => {
    if (ext === 'PDF') return 'bg-k-danger-soft text-k-danger-ink'
    if (ext === 'JPG' || ext === 'PNG' || ext === 'JPE') return 'bg-tone-blue text-tone-blue-ink'
    if (ext === 'XLS' || ext === 'XLSX') return 'bg-k-ok-soft text-k-ok'
    return 'bg-k-line text-k-ink-2'
}

export const NOTE_TYPES: NoteType[] = [
    { id: 'nota general', label: 'Nota', filter: 'nota', Icon: StickyNote2OutlinedIcon, tone: 'bg-tone-purple text-tone-purple-ink', placeholder: 'Agrega una nota a la bitácora de esta póliza…' },
    { id: 'llamada saliente', label: 'Llamada', filter: 'llamada', Icon: PhoneOutlinedIcon, tone: 'bg-tone-teal text-tone-teal-ink', placeholder: '¿De qué hablaron en la llamada?' },
    { id: 'correo', label: 'Correo', filter: 'correo', Icon: EmailOutlinedIcon, tone: 'bg-tone-blue text-tone-blue-ink', placeholder: 'Resumen del correo enviado o recibido…' },
    { id: 'platica con humano', label: 'Reunión', filter: 'reunion', Icon: EventOutlinedIcon, tone: 'bg-tone-orange text-tone-orange-ink', placeholder: 'Acuerdos de la reunión…' },
]

export const STATUS_LABELS: Record<string, string> = {
    'ot pendiente': 'OT Pendiente',
    'ot proceso': 'OT Proceso',
    vigente: 'Vigente',
    anticipada: 'Anticipada',
    gracia: 'Gracia',
    desprotegido: 'Desprotegido',
    vencida: 'Vencida',
    terminada: 'Terminada',
    renovada: 'Renovada',
    precancelada: 'Precancelada',
    cancelada: 'Cancelada',
    faltadetallepoliza: 'Falta detalle de póliza',
    faltadetallerecibos: 'Falta detalle de recibos',
    eliminada: 'Eliminada',
}

export const CONDUCTO_LABELS: Record<string, string> = {
    CXC: 'Cuentas por cobrar (CXC)',
    CAT: 'Cargo automático a tarjeta (CAT)',
    CUT: 'Cargo único a tarjeta (CUT)',
}

const LIVE = ['vigente', 'anticipada', 'gracia']
const CLOSED = ['cancelada', 'precancelada', 'terminada', 'eliminada', 'renovada']
const DANGER = ['vencida', 'desprotegido']

export type StatusTone = 'ok' | 'danger' | 'closed' | 'warn' | 'renew'

export interface StatusChip {
    label: string
    className: string
    tone: StatusTone
}

export interface VigenciaView {
    label: string
    progress: number
    barClass: string
    note: string
    noteClass: string
}

export interface RestanText {
    text: string
    urgent: boolean
}

export const statusLabel = (status?: string | null) => STATUS_LABELS[statusKey(status)] || labelize(status) || '—'

export const daysUntil = (value?: DateInput) => daysFromToday(value)

export const vigenciaProgress = (from?: DateInput, to?: DateInput) => {
    const start = parseDate(from)?.startOf('day')
    const end = parseDate(to)?.startOf('day')
    if (!start || !end) return 0
    const span = end.diff(start, 'milliseconds').milliseconds
    if (span <= 0) return 100
    const elapsed = DateTime.now().startOf('day').diff(start, 'milliseconds').milliseconds
    return Math.min(100, Math.max(0, Math.round((elapsed / span) * 100)))
}

export const vigenciaLabel = (from?: DateInput, to?: DateInput) => {
    const start = formatCompactDate(from)
    const end = formatCompactDate(to)
    if (!start && !end) return '—'
    return `${start || '—'} – ${end || '—'}`
}

const statusTone = (key: string): Omit<StatusChip, 'label'> => {
    if (LIVE.includes(key)) return { className: 'bg-k-ok-soft text-k-ok', tone: 'ok' }
    if (DANGER.includes(key)) return { className: 'bg-k-danger-soft text-k-danger-ink', tone: 'danger' }
    if (CLOSED.includes(key)) return { className: 'bg-k-line text-k-ink-2', tone: 'closed' }
    return { className: 'bg-tone-orange text-tone-orange-ink', tone: 'warn' }
}

const EXPIRED: StatusChip = { label: 'Vencida', className: 'bg-k-danger-soft text-k-danger-ink', tone: 'danger' }

/** Estatus del servicio tal cual; solo fuerza Vencida si sigue vigente pero ya terminó la cobertura. */
export const statusFromService = (status?: string | null, to?: DateInput): StatusChip => {
    const key = statusKey(status)
    const days = daysUntil(to)
    if (key === 'vigente' && days != null && days < 0) return EXPIRED
    return { label: statusLabel(status), ...statusTone(key) }
}

export const statusView = (status?: string | null, from?: DateInput, to?: DateInput): StatusChip => {
    const key = statusKey(status)
    const days = daysUntil(to)
    const progress = vigenciaProgress(from, to)
    if (key === 'vigente' && days != null && days < 0) return EXPIRED
    const renewing = LIVE.includes(key) && days != null && days >= 0 && (days <= 30 || progress >= 90)
    if (renewing) return { label: 'Por renovar', className: 'bg-tone-orange text-tone-orange-ink', tone: 'renew' }
    return { label: statusLabel(status), ...statusTone(key) }
}

const daysText = (days: number) => `${days} ${days === 1 ? 'día' : 'días'}`

export const vigenciaView = (from?: DateInput, to?: DateInput, status?: string | null): VigenciaView => {
    const progress = vigenciaProgress(from, to)
    const days = daysUntil(to)
    const tone = statusView(status, from, to).tone
    let barClass = 'bg-brand-teal'
    if (days != null && days < 0) barClass = 'bg-k-danger'
    else if (tone === 'closed') barClass = 'bg-k-muted-2'
    else if (tone === 'renew' || tone === 'danger' || progress >= 90) barClass = 'bg-brand-orange'
    let note = ''
    let noteClass = 'text-k-muted'
    if (days != null && days < 0 && tone !== 'closed') {
        note = `Sin cobertura hace ${daysText(Math.abs(days))}`
        noteClass = 'text-k-danger'
    } else if (days === 0 && tone !== 'closed') {
        note = 'Termina hoy'
        noteClass = 'text-k-danger'
    } else if (days != null && days > 0 && tone !== 'closed') {
        note = `Vence en ${daysText(days)}`
        noteClass = days <= 15 ? 'text-tone-orange-ink' : 'text-k-muted'
    }
    return { label: vigenciaLabel(from, to), progress, barClass, note, noteClass }
}

export const restanText = (status?: string | null, to?: DateInput): RestanText => {
    const key = statusKey(status)
    if (['cancelada', 'precancelada', 'eliminada'].includes(key)) return { text: 'Cancelada', urgent: false }
    if (key === 'terminada') return { text: 'Terminada', urgent: false }
    if (key === 'renovada') return { text: 'Renovada', urgent: false }
    const days = daysUntil(to)
    if (days == null) return { text: '', urgent: false }
    if (days < 0) return { text: `Venció hace ${daysText(Math.abs(days))}`, urgent: true }
    if (days === 0) return { text: 'Termina hoy', urgent: true }
    return { text: `Termina en ${daysText(days)}`, urgent: days <= 30 }
}

export const aseguradoraName = (poliza?: Pick<PolizaDTO, 'agenteClave'> | null) => {
    const aseguradora = poliza?.agenteClave?.aseguradora
    return aseguradora?.abreviacion || aseguradora?.aseguradora || '—'
}

export const vehicleLabel = (vehiculo?: PolizaVehiculoDTO | null) =>
    [vehiculo?.marca, vehiculo?.descripcion || vehiculo?.tipo, vehiculo?.modelo].filter(Boolean).join(' ')

export const currencyCode = (moneda?: string | Pick<MonedaDTO, 'abreviacion'> | null) => {
    if (!moneda) return 'MXN'
    if (typeof moneda === 'string') return moneda
    return moneda.abreviacion || 'MXN'
}

export const policyPath = (id: number) => `${POLICIES_PATH}/${id}`

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

const RENEWABLE = ['vigente', 'terminada', 'cancelada']

export const canRenewPolicy = (poliza?: Pick<PolizaDTO, 'polizaIDRenovo' | 'status'> | null) =>
    Boolean(poliza && !poliza.polizaIDRenovo && RENEWABLE.includes(statusKey(poliza.status)))

export const renewBlockedMessage = (poliza?: Pick<PolizaDTO, 'polizaIDRenovo'> | null) =>
    poliza?.polizaIDRenovo ? 'Esta póliza ya tiene renovación.' : 'Esta póliza no se puede renovar en su estatus actual.'

export const isPaidRecibo = (recibo: ReciboDTO) => ['pagado', 'conciliado'].includes(statusKey(recibo.status))
export const isPendingRecibo = (recibo: ReciboDTO) => statusKey(recibo.status) === 'pendiente'
export const isOverdueRecibo = (recibo: ReciboDTO) => isPendingRecibo(recibo) && (daysUntil(recibo.fecVence) ?? 0) < 0

export const sumTotal = (items: ReciboDTO[]) => items.reduce((total, item) => total + Number(item.total || 0), 0)

export const formatPct = (value: number) => value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })

export const sumComision = (items: ReciboDTO[]) => items.reduce((total, item) => total + Number(item.comision || 0), 0)

/** Comisión de la póliza; si el API no trae `comisiones`, se estima con `prima × porcentajeComision`. */
export const policyCommission = (poliza?: Pick<PolizaDTO, 'comisiones' | 'porcentajeComision' | 'prima'> | null) => {
    const pct = poliza?.porcentajeComision ?? null
    const monto = poliza?.comisiones ?? (pct != null ? (Number(poliza?.prima || 0) * pct) / 100 : null)
    return { pct, monto }
}

/** El API manda `recibo.comision` en 0: se prorratea la comisión de la póliza según la prima neta del recibo. */
export const reciboCommission = (recibo: ReciboDTO, poliza?: Pick<PolizaDTO, 'comisiones' | 'porcentajeComision' | 'prima'> | null) => {
    if (Number(recibo.comision || 0) > 0) return Number(recibo.comision)
    const prima = Number(recibo.prima || 0)
    const polizaPrima = Number(poliza?.prima || 0)
    const { pct, monto } = policyCommission(poliza)
    if (monto != null && polizaPrima > 0) return (Number(monto) * prima) / polizaPrima
    return pct != null ? (prima * pct) / 100 : 0
}

export const reciboChip = (recibo: ReciboDTO): Omit<StatusChip, 'tone'> => {
    const status = statusKey(recibo.status)
    if (status === 'pagado' || status === 'conciliado') return { label: labelize(status) || '', className: 'bg-k-ok-soft text-k-ok' }
    if (status === 'cancelado') return { label: 'Cancelado', className: 'bg-k-line text-k-ink-2' }
    if ((daysUntil(recibo.fecVence) ?? 0) < 0) return { label: 'Vencido', className: 'bg-k-danger-soft text-k-danger-ink' }
    return { label: 'Pendiente', className: 'bg-k-line text-k-ink-2' }
}

export const reciboPaidOn = (recibo: ReciboDTO) => (
    isPaidRecibo(recibo) && recibo.fecPago ? `Pagado ${formatSpokenDate(recibo.fecPago)}` : `Vence ${formatSpokenDate(recibo.fecVence) || '—'}`
)

export const nextPendingRecibo = (recibos: ReciboDTO[]) =>
    [...recibos.filter(isPendingRecibo)].sort((left, right) => String(left.fecVence || '').localeCompare(String(right.fecVence || '')))[0]

export const findCaratula = (files: AdjuntoDTO[]) =>
    files.find((item) => /car[aá]tula/i.test(item.archivo || ''))
    || files.find((item) => fileExt(item.archivo) === 'PDF')
    || files[0]

export const fetchPolicyAdjuntos = async (polizaId: number) => {
    const payload: WrapperResponse<AdjuntoDTO[]> | null = await (await APIInvoke.invokeGET(`/adjuntos/polizas/${polizaId}`)).json()
    if (!payload?.ok) throw new Error(payload?.message || 'No se pudieron cargar los adjuntos.')
    return payload.body || []
}

export const fetchReciboAdjuntos = async (reciboId: number) => {
    const payload: WrapperResponse<AdjuntoDTO[]> | null = await (await APIInvoke.invokeGET(`/adjuntos/recibos/${reciboId}`)).json()
    if (!payload?.ok) throw new Error(payload?.message || 'No se pudieron cargar los adjuntos del recibo.')
    return payload.body || []
}

export const reciboIdsOf = (poliza?: Pick<PolizaDTO, 'recibos'> | null) =>
    (poliza?.recibos || []).map((recibo) => recibo.id).filter((id): id is number => id != null)

/** `Recibo 2/12` para etiquetar adjuntos que pertenecen a un recibo. */
export const reciboTag = (recibos: ReciboDTO[], reciboId: number) => {
    const recibo = recibos.find((item) => item.id === reciboId)
    if (!recibo) return 'Recibo'
    return recibo.totalSerie ? `Recibo ${recibo.serie}/${recibo.totalSerie}` : `Recibo ${recibo.serie ?? ''}`.trim()
}

/** `/adjuntos/polizas/{id}` excluye los adjuntos de recibos (comprobantes de pago); se consultan por recibo y se mezclan. */
export const fetchPolicyAndReciboAdjuntos = async (polizaId: number, reciboIds: number[]) => {
    const [own, ...byRecibo] = await Promise.all([
        fetchPolicyAdjuntos(polizaId),
        ...reciboIds.map((reciboId) => fetchReciboAdjuntos(reciboId)),
    ])
    return [...own, ...byRecibo.flat()].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')))
}

export const uploadPolicyFiles = async (polizaId: number, files: File[]) => {
    const created: AdjuntoDTO[] = []
    for (const file of files) {
        if (file.size > MAX_UPLOAD_BYTES) throw new Error(`${file.name} supera 10 MB.`)
        const body = new FormData()
        body.append('bucket', config.bucket.name)
        body.append('path', `polizas/${polizaId}/${file.name}`)
        body.append('file', file)
        const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
        if (!uploaded.ok || !uploaded.body) throw new Error(uploaded.message || 'No se pudo subir el archivo.')
        const request: CreateAdjuntoRequest = {
            polizaId,
            dominio: '',
            path: uploaded.body.url,
            archivo: file.name,
        }
        const saved: WrapperResponse<AdjuntoDTO> = await (await APIInvoke.invokePOST('/adjuntos', request)).json()
        if (!saved.ok || !saved.body) throw new Error(saved.message || 'No se pudo registrar el archivo.')
        created.push(saved.body)
    }
    return created
}

export const deletePolicyAdjunto = async (id: number) => {
    const payload: WrapperResponse<unknown> | null = await (await APIInvoke.invokeDELETE(`/adjuntos/${id}`)).json()
    if (!payload?.ok) throw new Error(payload?.message || 'No se pudo eliminar el archivo.')
}

export const fetchPolicyBitacora = async (polizaId: number) => {
    const payload: WrapperResponse<CRMDTO[]> | null = await (await APIInvoke.invokeGET(`/crm?polizaId=${polizaId}`)).json()
    if (!payload?.ok) throw new Error(payload?.message || 'No se pudo cargar la bitácora.')
    const textos = payload.body?.[0]?.textos || []
    return [...textos].sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || '')))
}

export const createPolicyNote = async (poliza: Pick<PolizaDTO, 'id' | 'cliente'>, tipo: CRMTextoTipo, texto: string) => {
    const request: CreateCRMRequest = {
        asunto: 'bitacora',
        tipo,
        polizaId: poliza.id,
        clienteId: poliza.cliente?.id,
        textos: [{ tipo, texto }],
    }
    const payload: WrapperResponse<CRMDTO> | null = await (await APIInvoke.invokePOST('/crm', request)).json()
    if (!payload?.ok) throw new Error(payload?.message || 'No se pudo guardar la bitácora.')
    return payload.body?.textos?.[0] as CRMTextoDTO | undefined
}
